// BookingsManager — Beds24 bookings list with filters, presets, CSV/Excel export.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Calendar, Download, Loader2, Receipt, RefreshCw, Search,
} from 'lucide-react';
import { API_URL, apiCall, getToken } from './_shared';
import Beds24ArchiveCard from './Beds24ArchiveCard';
import BookingDetailModal from './BookingDetailModal';

const FiscalBadge = ({ st }) => {
  if (!st || st.status === 'none') return <span className="text-xs text-slate-400" data-testid="booking-fiscal-none">not invoiced</span>;
  if (st.status === 'fiscalized') return <span className="text-xs font-semibold text-emerald-700" data-testid="booking-fiscal-done">✓ {st.invoice_number}</span>;
  if (st.status === 'failed') return <span className="text-xs font-semibold text-red-600" data-testid="booking-fiscal-failed">failed · {st.invoice_number}</span>;
  return <span className="text-xs font-semibold text-amber-700" data-testid="booking-fiscal-issued">issued · not fiscalized</span>;
};

const statusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'confirmed': return 'bg-green-100 text-green-700';
    case 'new': return 'bg-blue-100 text-blue-700';
    case 'request': return 'bg-yellow-100 text-yellow-700';
    case 'cancelled': return 'bg-red-100 text-red-700';
    case 'black': return 'bg-gray-200 text-gray-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const formatSource = (src) => {
  if (src === null || src === undefined || src === '') return 'Direct';
  const s = String(src);
  const map = { '0': 'Direct', '1': 'Manual', '2': 'Booking.com', '19': 'Booking.com', '5': 'Airbnb', '7': 'Expedia' };
  return map[s] || s;
};

const BookingsManager = ({ userRole }) => {
  const [bookings, setBookings] = useState([]);
  const [fiscalMap, setFiscalMap] = useState({});
  const [selected, setSelected] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');
  const [stats, setStats] = useState({ total: 0, confirmed: 0, cancelled: 0, revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: dateRange.start, end_date: dateRange.end });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search.trim()) params.append('search', search.trim());
      const data = await apiCall(`/api/admin/bookings?${params.toString()}`);
      setBookings(data.bookings || []);
      setStats(data.stats || { total: 0, confirmed: 0, cancelled: 0, revenue: 0 });
      const ids = (data.bookings || []).map((b) => b.id).filter(Boolean);
      if (ids.length) apiCall(`/api/admin/fiscal/bookings/status?ids=${ids.join(',')}`).then((m) => setFiscalMap(m.map || {})).catch(() => {});
      setError(data.success ? null : (data.message || data.error));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange, statusFilter, search]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const applyPreset = (preset) => {
    const today = new Date();
    if (preset === 'upcoming') {
      setDateRange({
        start: today.toISOString().split('T')[0],
        end: new Date(today.getTime() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      });
    } else if (preset === 'past') {
      setDateRange({
        start: new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: today.toISOString().split('T')[0],
      });
    } else if (preset === 'all') {
      setDateRange({
        start: new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date(today.getTime() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      });
    } else if (preset === 'this_year') {
      const y = today.getFullYear();
      setDateRange({ start: `${y}-01-01`, end: `${y}-12-31` });
    }
  };

  const exportCSV = () => {
    const headers = ['ID', 'Guest', 'Email', 'Phone', 'Room', 'Check-in', 'Check-out', 'Adults', 'Children', 'Total', 'Currency', 'Status', 'Source', 'Country', 'Created'];
    const rows = bookings.map(b => [
      b.id, b.guest_name, b.email, b.phone, b.room_name,
      b.check_in, b.check_out, b.adults, b.children,
      b.total_price, b.currency, b.status, b.source, b.country, b.created_at,
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bookings_${dateRange.start}_to_${dateRange.end}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const exportXLSX = async () => {
    try {
      const params = new URLSearchParams({ start_date: dateRange.start, end_date: dateRange.end });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search.trim()) params.append('search', search.trim());
      const token = getToken();
      const resp = await fetch(`${API_URL}/api/admin/bookings/export.xlsx?${params.toString()}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `bookings_${dateRange.start}_to_${dateRange.end}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      alert('Excel export failed: ' + err.message);
    }
  };

  const invoiceable = (b) => !['cancelled', 'canceled', 'black'].includes((b.status || '').toLowerCase()) && Number(b.total_price) > 0 && (fiscalMap[String(b.id)]?.status || 'none') === 'none';
  const invoiceableRows = bookings.filter(invoiceable);
  const togglePick = (id) => setPicked((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const togglePickAll = () => setPicked((prev) => (prev.size === invoiceableRows.length ? new Set() : new Set(invoiceableRows.map((b) => String(b.id)))));

  const bulkIssue = async () => {
    const list = bookings.filter((b) => picked.has(String(b.id)));
    if (!list.length) return;
    const total = list.reduce((s, b) => s + Number(b.total_price || 0), 0);
    if (!window.confirm(`Issue ${list.length} sequential invoice(s) totalling €${total.toFixed(2)}? Numbers cannot be reused afterwards. Fiscalize them later in Finance → Invoice Register (bulk).`)) return;
    setBulkBusy(true); setBulkMsg('');
    try {
      const r = await apiCall('/api/admin/fiscal/bookings/bulk-issue', { method: 'POST', body: JSON.stringify({ bookings: list }) });
      const failed = r.results.filter((x) => x.status === 'failed').map((x) => `${x.guest_name || x.booking_id}: ${x.error}`);
      setBulkMsg(`${r.issued} issued · ${r.already_issued} already had an invoice · ${r.failed} failed${failed.length ? ` — ${failed.slice(0, 3).join(' · ')}${failed.length > 3 ? ' …' : ''}` : ''}`);
      setPicked(new Set());
      const ids = bookings.map((b) => b.id).filter(Boolean);
      apiCall(`/api/admin/fiscal/bookings/status?ids=${ids.join(',')}`).then((m) => setFiscalMap(m.map || {})).catch(() => {});
    } catch (e) { setBulkMsg(e.message); } finally { setBulkBusy(false); }
  };

  // Rows precomputed OUTSIDE JSX (Babel workaround).
  const rows = bookings.map((b, i) => (
    <tr key={b.id || i} className="border-b last:border-0 hover:bg-orange-50 cursor-pointer" onClick={() => setSelected(b)} data-testid={`booking-row-${b.id}`}>
      <td className="py-3 pl-4 pr-0 w-6" onClick={(e) => e.stopPropagation()}>
        {invoiceable(b) && <input type="checkbox" checked={picked.has(String(b.id))} onChange={() => togglePick(String(b.id))} aria-label={`Select booking ${b.id}`} data-testid={`booking-pick-${b.id}`} />}
      </td>
      <td className="py-3 px-4">
        <p className="font-medium text-gray-900">{b.guest_name}</p>
        <p className="text-xs text-gray-500">{b.email}</p>
        {b.country && <p className="text-xs text-gray-400">{b.city ? `${b.city}, ` : ''}{b.country}</p>}
      </td>
      <td className="py-3 px-4 text-sm text-gray-900">{b.room_name}</td>
      <td className="py-3 px-4 text-sm text-gray-600">{b.check_in}</td>
      <td className="py-3 px-4 text-sm text-gray-600">{b.check_out}</td>
      <td className="py-3 px-4 text-sm text-gray-600">{b.adults}A{b.children > 0 ? ` + ${b.children}C` : ''}</td>
      <td className="py-3 px-4 text-sm font-medium text-gray-900 text-right">€{Number(b.total_price || 0).toFixed(2)}</td>
      <td className="py-3 px-4 text-xs text-gray-500">{formatSource(b.source)}</td>
      <td className="py-3 px-4 text-center">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor(b.status)}`}>{b.status}</span>
      </td>
      <td className="py-3 px-4"><FiscalBadge st={fiscalMap[String(b.id)]} /></td>
    </tr>
  ));

  return (
    <div className="space-y-6" data-testid="bookings-manager">
      <Beds24ArchiveCard userRole={userRole} />
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Bookings (Beds24)</h2>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => applyPreset('upcoming')} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50" data-testid="preset-upcoming">Upcoming</button>
          <button onClick={() => applyPreset('past')} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50" data-testid="preset-past">Past Year</button>
          <button onClick={() => applyPreset('this_year')} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50" data-testid="preset-this-year">This Year</button>
          <button onClick={() => applyPreset('all')} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50" data-testid="preset-all">All</button>
          <button onClick={fetchBookings} className="flex items-center gap-2 px-4 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm" data-testid="bookings-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={exportCSV} disabled={!bookings.length} className="flex items-center gap-2 px-4 py-1.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm disabled:opacity-50" data-testid="bookings-export">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={exportXLSX} disabled={!bookings.length} className="flex items-center gap-2 px-4 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm disabled:opacity-50" data-testid="bookings-export-xlsx">
            <Download className="w-4 h-4" /> Excel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl p-4 border">
          <p className="text-xs text-gray-500">Total Bookings</p>
          <p className="text-2xl font-bold text-gray-900" data-testid="stat-total">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border">
          <p className="text-xs text-gray-500">Confirmed</p>
          <p className="text-2xl font-bold text-green-600" data-testid="stat-confirmed">{stats.confirmed}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border">
          <p className="text-xs text-gray-500">Cancelled</p>
          <p className="text-2xl font-bold text-red-500" data-testid="stat-cancelled">{stats.cancelled}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border">
          <p className="text-xs text-gray-500">Revenue</p>
          <p className="text-2xl font-bold text-orange-600" data-testid="stat-revenue">€{Number(stats.revenue || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 border flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Arrival between</span>
          <input type="date" value={dateRange.start} onChange={(e) => setDateRange(p => ({ ...p, start: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" data-testid="date-start" />
          <span className="text-gray-400">→</span>
          <input type="date" value={dateRange.end} onChange={(e) => setDateRange(p => ({ ...p, end: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" data-testid="date-end" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="status-filter">
          <option value="all">All statuses</option>
          <option value="confirmed">Confirmed</option>
          <option value="new">New</option>
          <option value="request">Request</option>
          <option value="cancelled">Cancelled</option>
          <option value="black">Blocked</option>
        </select>
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guest name or email..."
            className="flex-1 px-3 py-2 border rounded-lg text-sm"
            data-testid="bookings-search"
          />
        </div>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
          <p className="font-medium">Note: {error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : bookings.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No bookings found</h3>
          <p className="text-gray-500 mt-1">Try widening the date range or clearing filters</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {invoiceableRows.length > 0 && (
            <div className="bg-slate-900 text-white p-3 flex flex-wrap items-center gap-2 text-sm" data-testid="bulk-issue-bar">
              <span className="font-semibold" data-testid="bulk-issue-count">{picked.size} of {invoiceableRows.length} not-yet-invoiced bookings selected</span>
              <button type="button" onClick={togglePickAll} className="text-xs underline text-slate-300" data-testid="bulk-issue-select-all">{picked.size === invoiceableRows.length ? 'Clear' : 'Select all'}</button>
              <span className="flex-1" />
              <button type="button" onClick={bulkIssue} disabled={bulkBusy || !picked.size} className="inline-flex items-center gap-1 bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50" data-testid="bulk-issue">
                {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />} Issue invoices ({picked.size})
              </button>
              {bulkMsg && <div className="w-full text-xs text-slate-200" data-testid="bulk-issue-msg">{bulkMsg}</div>}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="bookings-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-3 pl-4 pr-0 w-6">{invoiceableRows.length > 0 && <input type="checkbox" checked={picked.size > 0 && picked.size === invoiceableRows.length} onChange={togglePickAll} aria-label="Select all invoiceable bookings" data-testid="booking-pick-all-header" />}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Guest</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Room</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Check-in</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Check-out</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Guests</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Total</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Source</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Fiscal</th>
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
        </div>
      )}
      {selected && <BookingDetailModal booking={selected} userRole={userRole} onClose={() => { setSelected(null); fetchBookings(); }} />}
    </div>
  );
};

export default BookingsManager;
